import { History, Clock, Sparkles, DollarSign, Briefcase, Heart, Eye, Shield, Crown, TriangleAlert, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const ANALYSIS_ICONS = { passado: History, presente: Clock, futuro: Sparkles };
const QUESTION_ICONS = {
  financeira: DollarSign,
  profissional: Briefcase,
  amor: Heart,
  espiritualidade: Eye,
  ataques: Shield,
  pactos: Crown,
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-sm text-[#A3A3AF] leading-relaxed font-light">
    <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0" />
    {children}
  </li>
);

export const Consulta = () => {
  const { t } = useLang();
  const c = t.consulta;

  return (
    <section id="consulta" data-testid="consultation-section" className="relative border-y border-[#22222B]">
      <div className="relative py-24 sm:py-36 overflow-hidden">
        <img
          src="/consulta-bg.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-[#0A0A0C]/60" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-6">{c.overline}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#F7F7F8] leading-snug mb-8">
              {c.title1} <span className="gold-text block">{c.title2}</span>
            </h2>
            <p className="text-[15px] sm:text-base text-[#D1D1DB] leading-relaxed font-light mb-6">{c.intro1}</p>
            <p className="text-sm text-[#A3A3AF] leading-relaxed font-light mb-10">{c.intro2}</p>
            <a
              data-testid="consultation-hero-cta"
              href={whatsappLink(c.waMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
            >
              {c.ctaHero} <ArrowRight size={15} />
            </a>
          </Reveal>
        </div>
      </div>

      <div className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center">
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{c.analysisOverline}</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug">{c.analysisTitle}</h3>
          </Reveal>
          <div data-testid="consultation-analysis-cards" className="mt-12 grid gap-5 md:grid-cols-3">
            {c.analysis.map(({ id, title, items }, i) => {
              const Icon = ANALYSIS_ICONS[id];
              return (
                <Reveal key={id} delay={i * 0.12}>
                  <div
                    data-testid={`analysis-card-${id}`}
                    className="h-full p-8 rounded-xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/40 transition-colors duration-500"
                  >
                    <span className="inline-flex w-11 h-11 rounded-sm border border-[#C5A059]/40 items-center justify-center mb-6">
                      <Icon size={19} className="text-[#D9A94A]" strokeWidth={1.4} />
                    </span>
                    <h4 className="font-heading text-lg tracking-[0.1em] uppercase text-[#F7F7F8] mb-5">{title}</h4>
                    <ul className="space-y-3">
                      {items.map((item) => (
                        <Bullet key={item}>{item}</Bullet>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <div data-testid="consultation-analysis-note" className="mt-8 p-6 rounded-xl border border-[#22222B] bg-[#121216] text-center">
              <p className="text-sm text-[#A3A3AF] font-light italic">
                <span className="text-[#D9A94A] not-italic font-semibold">{c.analysisNoteLabel}</span> {c.analysisNote}
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="py-20 sm:py-28 bg-[#121216] border-y border-[#22222B]">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center">
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{c.questionsOverline}</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug">{c.questionsTitle}</h3>
          </Reveal>
          <div data-testid="consultation-cards" className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {c.questions.map(({ id, title, items }, i) => {
              const Icon = QUESTION_ICONS[id];
              return (
                <Reveal key={id} delay={(i % 3) * 0.1}>
                  <div
                    data-testid={`consultation-theme-card-${id}`}
                    className="h-full p-7 rounded-xl bg-[#16161B] border border-[#22222B] hover:border-[#C5A059]/40 transition-colors duration-500"
                  >
                    <div className="flex items-center gap-3 mb-5">
                      <Icon size={20} className="text-[#D9A94A] shrink-0" strokeWidth={1.4} />
                      <h4 className="font-heading text-sm sm:text-[15px] tracking-[0.08em] uppercase text-[#F7F7F8] leading-snug">
                        {title}
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {items.map((item) => (
                        <Bullet key={item}>{item}</Bullet>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      <div className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal className="text-center">
            <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug">{c.howTitle}</h3>
          </Reveal>
          <div data-testid="consultation-how-grid" className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {c.how.map(({ label, value }, i) => (
              <Reveal key={label} delay={i * 0.08}>
                <div className="h-full p-6 rounded-xl bg-[#121216] border border-[#22222B] text-center">
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059] mb-2.5">{label}</p>
                  <p className="text-sm text-[#D1D1DB] font-light">{value}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div data-testid="consultation-warning-box" className="mt-14 max-w-2xl mx-auto p-7 sm:p-8 rounded-xl bg-[#16161B] border border-[#D9A94A]/25 flex items-start gap-5">
              <span className="inline-flex w-11 h-11 shrink-0 rounded-sm border border-[#D9A94A]/40 items-center justify-center">
                <TriangleAlert size={19} className="text-[#D9A94A]" strokeWidth={1.6} />
              </span>
              <div>
                <h4 className="font-heading text-base tracking-[0.12em] uppercase text-[#F7F7F8] mb-2.5">{c.warningTitle}</h4>
                <p className="text-sm text-[#A3A3AF] leading-relaxed font-light">{c.warning}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="pb-24 sm:pb-32">
        <Reveal className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug mb-5">{c.finalTitle}</h3>
          <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light mb-10">{c.finalText}</p>
          <a
            data-testid="consultation-cta-btn"
            href={whatsappLink(c.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
          >
            {c.cta} <ArrowRight size={15} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
